import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SpinnerDialogComponent } from 'src/app/core/components/dialogs/spinner-dialog/spinner-dialog.component';
import { ResponsiveBaseComponent } from 'src/app/core/components/responsive-base/responsive-base.component';
import { BusinessAccountModel } from 'src/app/models/business-account-model';
import { SpinnerService } from 'src/app/services/spinner.service';
import { NewsletterTemplateComponentStore } from 'src/app/stores/components/newsletter-templates/newsletter-template.component-store';
import { selectBusinessAccount, selectHasSubscriptionPlanOrder, selectUnpaidInvoice } from 'src/app/stores/global/app.selectors';

@Component({
  selector: 'app-newsletter-templates-page',
  templateUrl: './newsletter-templates-page.component.html',
  styleUrls: ['./newsletter-templates-page.component.scss'],
  standalone: false
})
export class NewsletterTemplatesPageComponent extends ResponsiveBaseComponent implements OnInit {
  public businessAccount$: Observable<BusinessAccountModel> = this.store.select(selectBusinessAccount);
  public unpaidInvoice$ = this.store.select(selectUnpaidInvoice);
  public viewModel$ = this.newsletterTemplateComponentStore.viewModel$;
  public showAdd: boolean;
  public currentPage: number = 0;
  public pageSize: number = 20;
  public pageSizeOptions = [this.pageSize];

  private readonly ngUnsubscribeState = new Subject<void>();
  private loadingSpinnerDialogRef: MatDialogRef<SpinnerDialogComponent>;
  private businessAccount: BusinessAccountModel;

  constructor(
    private readonly store: Store<any>,
    private readonly newsletterTemplateComponentStore: NewsletterTemplateComponentStore,
    private readonly router: Router,
    private readonly spinnerService: SpinnerService,
    responsive: BreakpointObserver,
  ) {
    super(responsive);
  }

  public ngOnInit(): void {
    this.store.select(selectBusinessAccount).pipe(filter(result => Boolean(result))).subscribe(businessAccount => {
      const gettingStartedCompanyDetails: boolean = !businessAccount.company.name || businessAccount.company.name === '';
      const gettingStartedCompanyLogo: boolean = !businessAccount.company.logo;
      if (gettingStartedCompanyDetails || gettingStartedCompanyLogo) {
        this.router.navigate(['/getting-started']);
      }
      else {
        this.businessAccount = businessAccount;

        this.newsletterTemplateComponentStore.setInitial();
        this.showSpinner();
        this.newsletterTemplateComponentStore.getNewsletterTemplates({ companyId: businessAccount.company.id, currentPage: this.currentPage, pageSize: this.pageSize });
      }
    });

    this.store.select(selectHasSubscriptionPlanOrder).pipe(takeUntil(this.ngUnsubscribeState)).subscribe((hasSubscriptionPlanOrder) => {
        this.showAdd = hasSubscriptionPlanOrder;
    });

    this.newsletterTemplateComponentStore.loaded$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((loaded) => {
      this.hideSpinner(loaded);
    });
  }

  public ngOnDestroy(): void {
    this.newsletterTemplateComponentStore.setInitial();
    this.ngUnsubscribeState.next();
    this.ngUnsubscribeState.complete();
  }

  public onPageEvent(event?: PageEvent): PageEvent {
    this.newsletterTemplateComponentStore.getNewsletterTemplates({ companyId: this.businessAccount.company.id, currentPage: event.pageIndex, pageSize: event.pageSize });
    return event;
  }

  public create(): void {
    this.router.navigate(['/companies/', this.businessAccount.company.id, 'newsletters', 'templates', 'create']);
  }

  public getDisplayedColumns(): string[] {
    if(this.isSmall() || this.isXSmall()){
      return ['id', 'name'];
    }
    return ['id', 'name', 'createdDate', 'updatedDate'];
  }

  private showSpinner() {
    this.loadingSpinnerDialogRef = this.spinnerService.show();
  }

  private hideSpinner(loaded: boolean) {
    if (loaded && this.loadingSpinnerDialogRef !== null) {
      this.spinnerService.hide(this.loadingSpinnerDialogRef);
      this.loadingSpinnerDialogRef = null;
    }
  }
}
